import {AuthModel} from './_models'

// A taxpayer who signed in through the tax-folder SSO (کارپوشه) registers their own business
// with nothing but the fiscal memory ID. The backend takes the identity from the SSO session and
// only accepts a memory whose owner is that identity; if the business is already in the panel,
// it refuses and sends the user to support (user decision, 2026-09-27).

export const SSO_BUSINESS_REGISTRATION_PATH = '/taxpayer/sso-register'
export const FISCAL_ID_GUIDE_URL = 'https://asatsp.ir/support/guides/user-registration'

type SsoUser = Partial<Pick<AuthModel, 'sso' | 'rl' | 'support'>> | null | undefined

export const isSsoSession = (user: SsoUser): boolean =>
  Boolean(user?.sso?.nid) && user?.support?.session_type !== 'support'

// Role -1 is a user without any business; an SSO user can also register it by the fiscal memory id on this page.
export const needsSsoBusinessRegistration = (user: SsoUser): boolean =>
  isSsoSession(user) && user?.rl === -1

export const normalizeFiscalIdInput = (value: string): string => {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹'
  const arabicDigits = '٠١٢٣٤٥٦٧٨٩'
  return (value || '')
    .replace(/[۰-۹]/g, (digit) => String(persianDigits.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(arabicDigits.indexOf(digit)))
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 6)
}

export const isCompleteFiscalId = (value: string): boolean => /^[0-9A-Z]{6}$/.test(value)

export type SsoRegistrationErrorCode =
  | 'sso_session_required'
  | 'sso_identity_mismatch'
  | 'sso_business_exists'
  | 'sso_business_already_yours'
  | 'identity_mismatch'
  | 'validation_failed'
  | 'captcha_failed'
  | string

export interface SsoRegistrationFailure {
  code?: SsoRegistrationErrorCode
  message: string
  traceId?: string
  existingBusiness?: {companyId: number; name: string}
  requiresFreshInquiry: boolean
  contactSupport: boolean
}

const freshInquiryCodes = new Set([
  'inquiry_expired',
  'inquiry_environment_changed',
  'inquiry_consumed',
  'inquiry_not_found',
  'inquiry_invalid',
  'inquiry_changed',
])

type ApiErrorBody = {
  code?: string
  message?: string
  traceId?: string
  existingBusiness?: {companyId: number; name: string}
}

export const readSsoRegistrationFailure = (
  error: unknown,
  fallback: string
): SsoRegistrationFailure => {
  const response = (error as {response?: {status?: number; data?: ApiErrorBody}})?.response
  if (!response) {
    return {
      message: 'ارتباط با سرور برقرار نشد. اینترنت خود را بررسی و دوباره تلاش کنید.',
      requiresFreshInquiry: false,
      contactSupport: false,
    }
  }

  const body = response.data && typeof response.data === 'object' ? response.data : {}
  const code = body.code
  return {
    code,
    message: body.message || fallback,
    traceId: body.traceId,
    existingBusiness: code === 'sso_business_already_yours' ? body.existingBusiness : undefined,
    requiresFreshInquiry: Boolean(code && freshInquiryCodes.has(code)),
    contactSupport: code === 'sso_business_exists' || code === 'identity_mismatch',
  }
}
